import { useMemo } from 'react';
import { useAIContext, aiValue } from './useAIContext';
import { useUser } from '@/hooks/user/useUser';
import { useUserSkills } from '@/hooks/user/useUserSkills';
import { useUserFavorites } from '@/hooks/user/useUserFavorites';
import { useUserSubscriptionResults } from '@/hooks/assessments/useUserSubscriptionResults';
import { useCheckFeatureAccessQuery } from '@/services/ApiService';

/**
 * Hook that adds persistent user context for the AI.
 * This should be used at a high level in the app (e.g., Layout component).
 *
 * User information is only stored in the AI context for users who have
 * chatbot feature access; otherwise no user data is fetched or merged.
 */
export const useAIUserContext = () => {
    const { user } = useUser();
    const { data: accessData } = useCheckFeatureAccessQuery('chatbot', { skip: !user });
    const hasAccess = accessData?.hasAccess ?? false;

    // Only fetch and store user data when the user has chatbot feature access.
    const skip = !user?.id || !hasAccess;
    const { powerSkills, specialtySkills, certifications, customSkills } = useUserSkills({ skip });
    const { occupations, colleges, programs, jobs, financialAid, apprenticeships } = useUserFavorites({ skip });
    const { data: subscriptionResults } = useUserSubscriptionResults({ skip });

    const firstName = user?.userProfile?.firstName ?? null;

    const userProfile = useMemo(() => {
        if (!user?.userProfile) return null;
        const p = user.userProfile;
        return {
            educationLevelId: p.educationLevelId,
            currentlyEmployed: p.currentlyEmployed,
            currentJobTitle: p.currentJobTitle,
            openToRemoteWork: p.openToRemoteWork,
            state: p.currentUsStateOrTerritoryId,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        user?.userProfile?.educationLevelId,
        user?.userProfile?.currentlyEmployed,
        user?.userProfile?.currentJobTitle,
        user?.userProfile?.openToRemoteWork,
        user?.userProfile?.currentUsStateOrTerritoryId,
    ]);

    const userSkills = useMemo(() => ({
        powerSkills: powerSkills.map(s => s.skillName),
        specialtySkills: specialtySkills.map(s => s.skillName),
        certifications: certifications.map(s => s.skillName),
        customSkills: customSkills.map(s => s.skillName),
    }), [powerSkills, specialtySkills, certifications, customSkills]);

    const userFavorites = useMemo(() => ({
        occupations: occupations.map(o => o.name),
        colleges: colleges.map(c => c.name),
        programs: programs.map(p => p.name),
        jobs: jobs.map(j => j.title),
        financialAid: financialAid.map(f => f.programName),
        apprenticeships: apprenticeships.map(a => a.name),
    }), [occupations, colleges, programs, jobs, financialAid, apprenticeships]);

    const assessmentResults = useMemo(() => {
        if (!subscriptionResults) return null;
        return subscriptionResults
            .filter(r => r.assessments.some(a => a.score?.scores?.length > 0))
            .map(r => ({
                name: r.assessmentName,
                category: r.assessmentCategory,
                scores: r.assessments
                    .filter(a => a.score?.scores?.length > 0)
                    .flatMap(a => a.score.scores.map(s => ({
                        name: s.name,
                        value: s.value,
                    }))),
            }));
    }, [subscriptionResults]);

    useAIContext(
        {
            userFirstName: aiValue(hasAccess ? firstName : null, 'The user\'s first name', '{firstName}'),
            userProfile: aiValue(hasAccess ? userProfile : null, 'The user\'s profile: education, employment, location'),
            userSkills: aiValue(hasAccess ? userSkills : null, 'The user\'s skills inventory by category'),
            userFavorites: aiValue(hasAccess ? userFavorites : null, 'The user\'s saved/favorited items'),
            assessmentResults: aiValue(hasAccess ? assessmentResults : null, 'The user\'s completed assessment results with scores'),
        },
        { persist: hasAccess },
    );
};
